import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import LoginScreen from '../components/LoginScreen.vue';
import ResetPasswordScreen from '../components/ResetPasswordScreen.vue';
import MessengerInterface from '../components/MessengerInterface.vue';
import SignUpFlow from '../components/SignUpFlow.vue';
import GoogleCalendarCallback from '../components/GoogleCalendarCallback.vue';
import MessengerFacebookCallback from '../components/MessengerFacebookCallback.vue';
import ServicesView from '../views/ServicesView.vue';
import SettingsView from '../views/SettingsView.vue';
import ClientsView from '../views/ClientsView.vue';
import VisitsView from '../views/VisitsView.vue';
import VisitDetailView from '../views/VisitDetailView.vue';
import ClientDetailView from '../views/ClientDetailView.vue';
import ActivationView from '../views/ActivationView.vue';
import CompleteSetupView from '../views/CompleteSetupView.vue';
import OAuthCallbackView from '../views/OAuthCallbackView.vue';
import { useAuth } from '../composables/useAuth';
import { initHeaderState, headerState } from '../composables/useHeaderState';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginScreen,
    meta: { requiresGuest: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordScreen,
    meta: { requiresGuest: true },
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: SignUpFlow,
    meta: { requiresGuest: true },
  },
  {
    path: '/activate',
    name: 'Activate',
    component: ActivationView,
    meta: { requiresGuest: true },
  },
  {
    path: '/auth/complete-setup',
    name: 'CompleteSetup',
    component: CompleteSetupView,
    meta: { requiresGuest: true },
  },
  {
    path: '/auth/oauth/callback',
    name: 'OAuthCallback',
    component: OAuthCallbackView,
    meta: { requiresGuest: true },
  },
  {
    // Kept as a redirect so links and bookmarks pointing at the old tab still land somewhere.
    path: '/conversations',
    redirect: '/messenger',
  },
  {
    path: '/messenger',
    name: 'Messenger',
    component: MessengerInterface,
    meta: { requiresAuth: true },
  },
  {
    path: '/messenger/:id',
    name: 'MessengerThread',
    component: MessengerInterface,
    meta: { requiresAuth: true },
  },
  {
    path: '/google-calendar/callback',
    name: 'GoogleCalendarCallback',
    component: GoogleCalendarCallback,
    meta: { requiresAuth: true },
  },
  {
    path: '/messenger/connect',
    name: 'MessengerFacebookCallback',
    component: MessengerFacebookCallback,
    meta: { requiresAuth: true },
  },
  {
    path: '/services',
    name: 'Services',
    component: ServicesView,
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/clients',
    name: 'Clients',
    component: ClientsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/visits',
    name: 'Visits',
    component: VisitsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/visits/:id',
    name: 'VisitDetail',
    component: VisitDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/clients/:id',
    name: 'ClientDetail',
    component: ClientDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('../views/OnboardingView.vue'),
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const token = localStorage.getItem('token');
  const { user, fetchUser } = useAuth();

  if (to.meta.requiresAuth) {
    // Protected route - verify token
    if (!token) {
      next('/login');
      return;
    }

    // If user data not loaded, try to fetch it
    if (!user.value) {
      const userData = await fetchUser();
      if (!userData) {
        // Token invalid or expired
        next('/login');
        return;
      }
    }

    // Initialize header state (idempotent - fetches calendar, messenger, services status)
    await initHeaderState(user.value!.id, user.value!.organization_id);

    // Onboarding check
    const messengerRequired = headerState.messengerSourceId.value !== null;
    const isOnboardingComplete =
      !!user.value!.personality &&
      !!user.value!.tone_style &&
      headerState.isCalendarConnected.value &&
      (!messengerRequired || headerState.isMessengerConnected.value) &&
      headerState.hasServices.value;

    if (
      to.name !== 'Onboarding' &&
      to.name !== 'GoogleCalendarCallback' &&
      to.name !== 'MessengerFacebookCallback'
    ) {
      const skipped = sessionStorage.getItem('onboarding_skipped');
      if (!skipped && !isOnboardingComplete) {
        next('/onboarding');
        return;
      }
    } else if (isOnboardingComplete) {
      // Already complete — no need to be on onboarding
      next('/messenger');
      return;
    }

    next();
  } else if (to.meta.requiresGuest) {
    // Guest route - redirect to messenger if authenticated
    if (token && user.value) {
      next('/messenger');
    } else if (token) {
      // Token exists but user not loaded, verify it
      const userData = await fetchUser();
      if (userData) {
        next('/messenger');
      } else {
        next();
      }
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
